import React, { useState } from "react";
import { Button, Card, Form } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import moment from "moment";
import InputText from "src/components/Forms/InputText";

const StatsPeriodFilters = ({ from, to, onApply, loading }) => {
  const { t } = useTranslation("dashboard");
  const [draftFrom, setDraftFrom] = useState(from);
  const [draftTo, setDraftTo] = useState(to);
  const [preset, setPreset] = useState("custom");
  const hasChanges = draftFrom !== from || draftTo !== to;
  let validationError;

  if (!draftFrom || !draftTo) {
    validationError = t("stats.filters.validation.required");
  } else if (
    !moment(draftFrom, "YYYY-MM-DD", true).isValid() ||
    !moment(draftTo, "YYYY-MM-DD", true).isValid()
  ) {
    validationError = t("stats.filters.validation.invalid");
  } else if (draftFrom > draftTo) {
    validationError = t("stats.filters.validation.order");
  }

  const canApply = hasChanges && !validationError && !loading;

  function selectPreset(event) {
    const value = event.target.value;
    setPreset(value);

    if (value === "custom") return;

    const { unit, offset } = {
      current_month: { unit: "month", offset: 0 },
      previous_month: { unit: "month", offset: 1 },
      current_year: { unit: "year", offset: 0 },
      previous_year: { unit: "year", offset: 1 },
    }[value];
    const period = moment().subtract(offset, unit);

    setDraftFrom(period.clone().startOf(unit).format("YYYY-MM-DD"));
    setDraftTo((offset ? period.endOf(unit) : period).format("YYYY-MM-DD"));
  }

  function changeFrom(event) {
    setDraftFrom(event.target.value);
    setPreset("custom");
  }

  function changeTo(event) {
    setDraftTo(event.target.value);
    setPreset("custom");
  }

  function applyPeriod(event) {
    event.preventDefault();

    if (canApply) {
      onApply({ from: draftFrom, to: draftTo });
    }
  }

  return (
    <Card as="section" className="shadow-sm mb-4">
      <Card.Body className="p-3">
        <h2 className="h6 mb-3">
          {t("stats.filters.title")}
        </h2>
        <Form onSubmit={applyPeriod} noValidate>
          <Form.Row className="align-items-end">
            <Form.Group
              controlId="stats-period-preset"
              className="col-12 col-md-4 col-lg-3"
            >
              <Form.Label>{t("stats.filters.preset")}</Form.Label>
              <Form.Control as="select" value={preset} onChange={selectPreset}>
                {[
                  "custom",
                  "current_month",
                  "previous_month",
                  "current_year",
                  "previous_year",
                ].map((value) => (
                  <option key={value} value={value}>
                    {t(`stats.filters.presets.${value}`)}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>
            <Form.Group
              controlId="stats-period-from"
              className="col-12 col-sm-6 col-md-4 col-lg-3"
            >
              <Form.Label>{t("stats.filters.from")}</Form.Label>
              <InputText
                name="from"
                type="date"
                value={draftFrom}
                max={draftTo || undefined}
                onChange={changeFrom}
              />
            </Form.Group>
            <Form.Group
              controlId="stats-period-to"
              className="col-12 col-sm-6 col-md-4 col-lg-3"
            >
              <Form.Label>{t("stats.filters.to")}</Form.Label>
              <InputText
                name="to"
                type="date"
                value={draftTo}
                min={draftFrom || undefined}
                onChange={changeTo}
              />
            </Form.Group>
            <Form.Group className="col-12 col-lg-3">
              <Button type="submit" className="w-100" disabled={!canApply}>
                {t("stats.filters.apply")}
              </Button>
            </Form.Group>
          </Form.Row>
          {validationError && (
            <p className="small text-danger mb-2">{validationError}</p>
          )}
          <div className="small text-muted">
            {t("stats.filters.applied", {
              from: moment(from, "YYYY-MM-DD").format("L"),
              to: moment(to, "YYYY-MM-DD").format("L"),
            })}
          </div>
        </Form>
      </Card.Body>
    </Card>
  );
};

export default StatsPeriodFilters;
